import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Loader2, ImageIcon, Upload, Crop, Move, ChevronLeft, Maximize, AlertTriangle, MapPin } from "lucide-react";
import { MEDIA_CLASS_LABEL } from "@/components/AdaptiveMediaFrame";

// Adaptive Media V3 — Marketplace Cover (outcome-oriented). Source Media != Marketplace Cover.
// Paths: (A) Use a Photo [FILL focal/zoom], (B) Upload a Cover [FILL], (C) Create from Creative
// [FILL crop of a MARKETING_CREATIVE], (D) Show Entire Image [FIT, secondary escape hatch].
// Informational classes (floor plan/map/price/brochure) are poor covers → non-blocking recommendation;
// never silently cropped, never removed from the gallery.
const INFORMATIONAL = ["FLOOR_PLAN", "MAP", "PRICE_SHEET", "BROCHURE"];
const FG_PAD = "2.5%"; // mirrors server 0.95 FIT foreground margin
const peso = (n) => (n || n === 0) ? "₱" + Number(n).toLocaleString() : "";

export function CoverComposer({ open, onOpenChange, photos = [], coverId, listing = {}, onCommit }) {
  const coverPhoto = photos.find((p) => p.id === coverId) || null;
  const photoAssets = photos.filter((p) => (p.media_class || "PHOTO") === "PHOTO");
  const creatives = photos.filter((p) => (p.media_class || "PHOTO") === "MARKETING_CREATIVE");
  const coverIsInformational = coverPhoto && INFORMATIONAL.includes(coverPhoto.media_class);

  const [step, setStep] = useState("choose");      // choose | pick-photo | pick-creative | frame
  const [saving, setSaving] = useState(false);
  // working selection
  const [work, setWork] = useState(null);          // { photoId, url, isUpload, file }
  const [fitMode, setFitMode] = useState(false);   // true = "Show entire image" (FIT)
  const [focal, setFocal] = useState({ x: 0.5, y: 0.5 });
  const [zoom, setZoom] = useState(1);
  const [bg, setBg] = useState("BRAND");
  const frameRef = useRef(null);
  const dragging = useRef(false);

  const reset = () => { setStep("choose"); setWork(null); setFitMode(false); setFocal({ x: 0.5, y: 0.5 }); setZoom(1); setBg("BRAND"); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const startFrame = (sel, { fit = false } = {}) => {
    const spec = sel.presentations?.marketplace_card_4x3;
    setWork(sel);
    setFitMode(fit);
    setFocal(spec?.focal || { x: 0.5, y: 0.5 });
    setZoom(spec?.zoom || 1);
    setBg(spec?.bg || "BRAND");
    setStep("frame");
  };

  const onUploadInput = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    startFrame({ photoId: null, url: URL.createObjectURL(file), isUpload: true, file });
  };

  const setFocalFromEvent = (e) => {
    const el = frameRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    setFocal({ x: Math.min(1, Math.max(0, cx / r.width)), y: Math.min(1, Math.max(0, cy / r.height)) });
  };

  const save = async () => {
    setSaving(true);
    const body = fitMode
      ? { surface: "marketplace_card_4x3", mode: "FIT", bg }
      : { surface: "marketplace_card_4x3", mode: "FILL", focal: { x: +focal.x.toFixed(3), y: +focal.y.toFixed(3) }, zoom: +zoom.toFixed(2) };
    try {
      await onCommit({ file: work?.file || null, pickPhotoId: work?.isUpload ? null : work?.photoId, body });
      close(false);
    } catch (e) {
      // error already surfaced via toast in commitCover; keep the composer open for retry
    } finally { setSaving(false); }
  };

  // ---------- render helpers ----------
  const CardPreview = ({ children }) => (
    // actual Marketplace card geometry (image area + info strip), not an isolated crop
    <div data-testid="composer-preview" className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm max-w-[320px] mx-auto">
      <div ref={frameRef} className="relative w-full aspect-[4/3] overflow-hidden bg-[#0F2C59] select-none"
        style={!fitMode ? { cursor: dragging.current ? "grabbing" : "grab" } : undefined}
        onMouseDown={!fitMode ? (e) => { dragging.current = true; setFocalFromEvent(e); } : undefined}
        onMouseMove={!fitMode ? (e) => { if (dragging.current) setFocalFromEvent(e); } : undefined}
        onMouseUp={() => { dragging.current = false; }} onMouseLeave={() => { dragging.current = false; }}
        onTouchStart={!fitMode ? setFocalFromEvent : undefined} onTouchMove={!fitMode ? setFocalFromEvent : undefined}>
        {children}
      </div>
      <div className="p-3">
        <div className="text-[13px] font-bold text-[#0F2C59]">{peso(listing.price) || "₱ —"}</div>
        <div className="text-[12px] text-slate-700 truncate">{listing.title || "Your listing title"}</div>
        <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{listing.city || listing.location || "Location"}</div>
      </div>
    </div>
  );

  const OptionCard = ({ testid, icon: Icon, title, desc, onClick, disabled }) => (
    <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
      className={`w-full text-left rounded-xl border-2 px-4 py-3 flex items-start gap-3 transition-colors ${disabled ? "opacity-40 cursor-not-allowed border-slate-200" : "border-slate-200 hover:border-[#0F2C59] hover:bg-[#0F2C59]/5"}`}>
      <span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-[#0F2C59]/10 text-[#0F2C59] flex items-center justify-center"><Icon className="h-5 w-5" /></span>
      <span><span className="block font-semibold text-sm text-slate-800">{title}</span><span className="block text-[12px] text-slate-500 mt-0.5">{desc}</span></span>
    </button>
  );

  const PhotoGrid = ({ items, onPick, emptyLabel }) => (
    items.length ? (
      <div className="grid grid-cols-3 gap-2">
        {items.map((p) => (
          <button key={p.id} type="button" data-testid={`composer-pick-${p.id}`} onClick={() => onPick(p)}
            className="relative aspect-[4/3] rounded-lg overflow-hidden border-2 border-slate-200 hover:border-[#0F2C59]">
            <img src={p.thumb_url || p.url} alt="" className="w-full h-full object-cover" />
            {p.id === coverId && <span className="absolute bottom-1 left-1 text-[9px] font-semibold bg-[#C79A3B] text-white px-1 rounded">Current</span>}
          </button>
        ))}
      </div>
    ) : <p className="text-sm text-slate-500 py-6 text-center">{emptyLabel}</p>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cover-composer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step !== "choose" && <button data-testid="composer-back" onClick={() => setStep("choose")} className="text-slate-400 hover:text-slate-700"><ChevronLeft className="h-5 w-5" /></button>}
            Marketplace Cover
          </DialogTitle>
          <DialogDescription>A 4:3 discovery visual for this listing. Your original photos and creatives stay untouched in the gallery.</DialogDescription>
        </DialogHeader>

        {step === "choose" && (
          <div className="space-y-3" data-testid="composer-choose">
            {coverIsInformational && (
              <div data-testid="composer-informational-reco" className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12px] text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Your cover is a {MEDIA_CLASS_LABEL[coverPhoto.media_class] || coverPhoto.media_class}. Choose a property photo or create a Marketplace Cover for a better Marketplace presentation. (The {MEDIA_CLASS_LABEL[coverPhoto.media_class]} stays in your gallery.)</span>
              </div>
            )}
            <OptionCard testid="composer-opt-photo" icon={ImageIcon} title="Use a Photo"
              desc="Choose one of the property's photos and frame it for Marketplace." onClick={() => setStep("pick-photo")} />
            <OptionCard testid="composer-opt-upload" icon={Upload} title="Upload a Cover"
              desc="Upload a dedicated Marketplace cover (also added to your gallery)." onClick={() => document.getElementById("composer-upload-input")?.click()} />
            {creatives.length > 0 && (
              <OptionCard testid="composer-opt-creative" icon={Crop} title="Create from Creative"
                desc="Turn an existing marketing creative into a 4:3 cover by positioning/cropping it." onClick={() => creatives.length === 1 ? startFrame(creatives[0]) : setStep("pick-creative")} />
            )}
            <input id="composer-upload-input" type="file" accept="image/*" className="hidden" onChange={onUploadInput} />
            <details className="pt-1">
              <summary data-testid="composer-more-options" className="text-[12px] text-slate-500 cursor-pointer select-none">More options</summary>
              <div className="pt-2">
                <OptionCard testid="composer-opt-entire" icon={Maximize} title="Show Entire Image"
                  desc="Fit the whole current cover inside the 4:3 frame on a branded background. Not recommended for posters — content can look small."
                  onClick={() => coverPhoto && startFrame(coverPhoto, { fit: true })} disabled={!coverPhoto} />
              </div>
            </details>
          </div>
        )}

        {step === "pick-photo" && (
          <div className="space-y-2" data-testid="composer-pick-photo">
            <p className="text-[12px] text-slate-500">Pick a property photo to frame as your cover.</p>
            <PhotoGrid items={photoAssets} onPick={(p) => startFrame(p)} emptyLabel="No property photos yet. Upload a cover or a photo first." />
          </div>
        )}

        {step === "pick-creative" && (
          <div className="space-y-2" data-testid="composer-pick-creative">
            <p className="text-[12px] text-slate-500">Pick a creative to crop into a 4:3 cover.</p>
            <PhotoGrid items={creatives} onPick={(p) => startFrame(p)} emptyLabel="No marketing creatives on this listing." />
          </div>
        )}

        {step === "frame" && work && (
          <div className="space-y-3" data-testid="composer-frame">
            <CardPreview>
              {fitMode ? (
                <>
                  {bg === "EXTENDED" && (<><img src={work.url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60" /><div className="absolute inset-0 bg-black/20" /></>)}
                  <img src={work.url} alt="cover" className="absolute inset-0 w-full h-full object-contain" style={{ padding: FG_PAD }} />
                </>
              ) : (
                <>
                  <img src={work.url} alt="cover" draggable={false} className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: `${focal.x * 100}% ${focal.y * 100}%`, transform: zoom > 1 ? `scale(${zoom})` : undefined }} />
                  <span className="pointer-events-none absolute h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md" style={{ left: `${focal.x * 100}%`, top: `${focal.y * 100}%` }} />
                  <span className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 text-[11px] font-medium text-white/90 bg-black/35 px-2 py-0.5 rounded-full"><Move className="h-3 w-3" /> Drag to reposition</span>
                </>
              )}
            </CardPreview>

            {!fitMode ? (
              <div data-testid="composer-fill-controls">
                <div className="flex items-center justify-between text-xs font-medium text-slate-600 mb-1"><span>Zoom</span><span>{zoom.toFixed(2)}×</span></div>
                <Slider data-testid="composer-zoom" min={1} max={3} step={0.05} value={[zoom]} onValueChange={(v) => setZoom(v[0])} />
                {work.isUpload && <p className="text-[11px] text-slate-400 mt-2">This image will also appear in your listing gallery.</p>}
              </div>
            ) : (
              <div data-testid="composer-fit-controls">
                <span className="text-xs font-medium text-slate-600">Background</span>
                <div className="flex gap-2 mt-1.5">
                  <button type="button" data-testid="composer-bg-brand" onClick={() => setBg("BRAND")} className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold ${bg === "BRAND" ? "border-[#0F2C59] bg-[#0F2C59]/5" : "border-slate-200"}`}>Brand navy</button>
                  <button type="button" data-testid="composer-bg-extended" onClick={() => setBg("EXTENDED")} className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold ${bg === "EXTENDED" ? "border-[#0F2C59] bg-[#0F2C59]/5" : "border-slate-200"}`}>Blurred extension</button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" data-testid="composer-cancel" onClick={() => close(false)} disabled={saving}>Cancel</Button>
              <Button data-testid="composer-save" onClick={save} disabled={saving} className="bg-[#0F2C59] hover:bg-[#16407f]">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save cover"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CoverComposer;
